import React, { useEffect, useState, useRef } from 'react';
import { Box as MuiBox, InputAdornment, useTheme } from '@mui/material';
import { FormApi } from 'final-form';
import { Field } from 'react-final-form';
import { format, isSameDay } from 'date-fns';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { values as toValues } from 'ramda';
import styled from 'styled-components';

import FormFileUploader from 'components/ui/FormFileUploader';
import FormAutocomplete from 'components/ui/FormAutocomplete';
import { DateField } from '../styled/appointmentDialog';
import useLoadPatients from './hooks/useLoadPatients';
import { getDateFormat } from 'lib/utils';
import useDialog from 'lib/hooks/useDialog';
import { meetingTypesData } from 'lib/enums/meetingTypes';
import {
  FormInput,
  Icon,
  FormRadioIconButton,
  Typography,
  FormControl,
  Switch,
} from 'components/ui';
import ChooseDateDialog from '../ChooseDateDialog';
import { FormFieldsContainer } from './styled/layout';
import FormPatientAutocomplete from 'components/ui/FormPatientAutocomplete';
import { useOrgUnitMembers } from '../hooks/useOrgUnitMembers';
import useGetModesAutocomplete from 'pagesAdmin/SettingsPage/TabRooms/hooks/useGetModesAutocomplete';
import useGetRoomsAutocomplete from 'pagesAdmin/SettingsPage/TabRooms/hooks/useGetRoomsAutocomplete';
import {
  ReservationMode,
  RoomItem,
} from 'pagesAdmin/SettingsPage/TabRooms/types';
import { formatInTimeZone } from 'date-fns-tz';
import { dateFnsLocales } from 'lib/utils/getDateFnsLocales';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const AppointmentForm = (props: Props) => {
  const { form, setSearch } = props;
  const chooseDateDialog = useDialog();
  const { t, i18n } = useTranslation();
  const theme = useTheme()
  const dateRef = useRef<any>(null);

  const values = form.getState().values;

  const [uid, setUid] = useState<string | null | undefined>(
    values?.teamMember?.id
  );

  useEffect(() => {
    if (values?.teamMember?.id) setUid(values.teamMember.id);
  }, [values?.teamMember?.id]);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState(
    values?.startDate
      ? {
          startDate: getDateFormat(values.startDate),
          endDate: getDateFormat(values.endDate),
        }
      : {}
  );
  const [selectedDuration, setSelectedDuration] = useState(30);

  const [isRangeOfDates, setIsRangeOfDates] = useState(false);

  useEffect(() => {
    setSelectedTimeSlot({});
    form.change('locationId', undefined);
    form.change('date', undefined);
  }, [values?.teamMember]);

  useEffect(() => {
    form.change('room', undefined);
    // Reset room search when location changes
    setSearchRooms('');
  }, [values?.locationId, values?.isOnline]);

  useEffect(() => {
    setSelectedDuration(values?.reservationMode?.duration || 30);
    form.change('room', undefined);
    // Reset room search when service type changes
    setSearchRooms('');
  }, [values?.reservationMode]);

  useEffect(() => {
    setSelectedTimeSlot({});
    form.change('date', undefined);
  }, [values?.room]);

  const selectedTimeSlotString = selectedTimeSlot.startDate
    ? isRangeOfDates
      ? format(getDateFormat(selectedTimeSlot.startDate), 'P', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        }) +
        ' ' +
        format(getDateFormat(selectedTimeSlot.startDate), 'p', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        }) +
        ' - ' +
        format(getDateFormat(selectedTimeSlot.endDate), 'P', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        }) +
        ' ' +
        format(getDateFormat(selectedTimeSlot.endDate), 'p', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        })
      : format(getDateFormat(selectedTimeSlot.startDate), 'P', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        }) +
        ' ' +
        format(getDateFormat(selectedTimeSlot.startDate), 'p', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        }) +
        ' - ' +
        format(getDateFormat(selectedTimeSlot.endDate), 'p', {
          //@ts-ignore
          locale: dateFnsLocales[i18n.language],
        })
    : '';

  const [searchPatients, setSearchPatients] = useState('');
  const [debouncedSearchPatients] = useDebounce(searchPatients, 200);

  const [searchDoctors, setSearchDoctors] = useState('');
  const [debouncedSearchDoctors] = useDebounce(searchDoctors, 200);

  const { orgUnitMembers, fetchMoreData, orgUnitsLoading } = useOrgUnitMembers({
    name: debouncedSearchDoctors,
    roleTypes: ['MEDICAL'],
  });
  const { patients, fetchData, patientsLoading } = useLoadPatients(
    debouncedSearchPatients
  );

  const openChooseDateDialog = async (form: FormApi) => {
    const {
      selectedTime,
      planifiedDuration,
      isRange,
    } = await chooseDateDialog.open({
      selectedTimeSlot,
      selectedDuration,
      isRangeOfDates,
      form,
      reservationModeId: values?.reservationMode?.id,
      roomId: values?.room?.id,
    });

    if (!selectedTime) return;

    setSelectedTimeSlot(selectedTime);
    setSelectedDuration(planifiedDuration);
    setIsRangeOfDates(isRange);
  };

  const {
    rooms,
    setSearch: setSearchRooms,
    loading: loadingRooms,
    loadMore: loadMoreRooms,
  } = useGetRoomsAutocomplete({
    filter: {
      locationId: values?.isOnline ? undefined : values?.locationId || 'null', // values?.locationId is mandatory if !isOnline, "null" is used to return no values
      reservationModeId: values?.reservationMode?.id || 'null', // values?.reservationMode is mandatory to select rooms, "null" is used to return no values
    },
  });

  const {
    modes,
    setSearch: setSearchModes,
    loading: loadingModes,
    loadMore: loadMoreModes,
  } = useGetModesAutocomplete();

  return (
    <>
      <FormFieldsContainer>
        <FormControl label={t('Doctor')} data-id="team-member-container">
          <FormPatientAutocomplete
            name="teamMember"
            isDoctor
            options={orgUnitMembers}
            loading={orgUnitsLoading}
            onScroll={fetchMoreData}
            onChange={(e: any) => setSearchDoctors(e.target.value)}
          />
        </FormControl>

        <FormControl label={t('Patient')} data-id="patient-container">
          <FormPatientAutocomplete
            name="patient"
            placeholder={t('Select patient')}
            label={t('Select a patient')}
            isPatient
            options={patients}
            onScroll={fetchData}
            loading={patientsLoading}
            onChange={(e: any) => setSearchPatients(e?.target?.value)}
          />
        </FormControl>

        <FormControl
          label={t('Type of appointment')}
          data-id="event-type-container"
        >
          <FormAutocomplete
            name="eventType"
            options={toValues(meetingTypesData)}
            placeholder={t('Choose event type')}
            getOptionLabel={(option: any) => t(option?.label)}
            getOptionValue={(option: any) => option?.value}
          />
        </FormControl>

        <MuiBox style={{ marginBottom: 15 }} data-id="meeting-format-container">
          <MuiBox
            style={{
              display: 'flex',
              gap: 12,
            }}
          >
            <FormRadioIconButton
              icon="hospital"
              type="fal"
              label={t('On-site')}
              name="isOnline"
              value={false}
            />
            <FormRadioIconButton
              icon="globe"
              type="fal"
              label={t('Online')}
              name="isOnline"
              value={true}
            />
          </MuiBox>
        </MuiBox>

        {values.isOnline ? (
          <MuiBox data-id="meeting-place-container" mb="15px">
            <STypography>
              <Icon icon="link" style={{ marginRight: 10 }} />
              {t('Your appointment link will be generated')}
            </STypography>
          </MuiBox>
        ) : (
          <>
            <MuiBox data-id="meeting-place-container" mb="15px">
              <STypography>
                <Icon icon="clock" style={{ marginRight: 10 }} />
                {t('Organization might have a different timezone than yours')}
              </STypography>
            </MuiBox>
            <FormControl
              label={t('Clinic address or location')}
              data-id="meeting-place-container"
              color={!values.teamMember ? theme.palette.colors.gray : theme.palette.colors.black}
              disabled={!values.teamMember}
            >
              <FormAutocomplete
                name="locationId"
                options={toValues(values?.teamMember?.subOrganizations)}
                placeholder={t('Add address')}
                getOptionLabel={(option: any) => option?.name}
                getOptionValue={(option: any) => option?.location.id}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="map-marked" style={{ marginLeft: 10 }} />
                    </InputAdornment>
                  ),
                }}
                disabled={!values.teamMember}
              />
            </FormControl>
          </>
        )}

        {/* #DO NOT DELETE - Service Type and Room Selection - Hidden per request */}
        {/* <FormControl
          label={`${t('Mode')} (${t('optional')})`}
          color={
            !values.isOnline && !values.locationId
              ? theme.palette.colors.gray
              : theme.palette.colors.black
          }
          disabled={!values.isOnline && !values.locationId}
        >
          <FormAutocomplete
            name="reservationMode"
            options={modes}
            loading={loadingModes}
            onScroll={loadMoreModes}
            inputOnChange={(v: string) => setSearchModes(v)}
            placeholder={t('Choose a mode')}
            getOptionLabel={(option: RoomItem) => t(option?.name)}
            getOptionValue={(option: RoomItem) => option}
            disabled={!values.isOnline && !values.locationId}
          />
        </FormControl>

        {!values.isOnline && (
          <FormControl
            label={`${t('Room')} (${t('optional')})`}
            color={!values.reservationMode ? theme.palette.colors.gray : theme.palette.colors.black}
            disabled={!values.reservationMode}
            style={{ 
              display: values.reservationMode ? 'block' : 'none'
            }}
          >
            <FormAutocomplete
              name="room"
              options={rooms}
              loading={loadingRooms}
              onScroll={loadMoreRooms}
              inputOnChange={(v: string) => setSearchRooms(v)}
              placeholder={t('Choose a room')}
              getOptionLabel={(option: ReservationMode) => t(option?.name)}
              getOptionValue={(option: ReservationMode) => option}
              disabled={!values.reservationMode}
            />
          </FormControl>
        )} */}

        {values?.teamMember && (
          <FormControl
            label={t('Date and time')}
            data-id="date-field-container"
          >
            <DateField
              fullWidth
              name="date"
              multiline={isRangeOfDates}
              selectedTimeSlot={selectedTimeSlotString}
              onClick={() => openChooseDateDialog(form)}
              placeholder={t('Choose date')}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="calendar" style={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            {values?.locationId &&
            values?.teamMember?.subOrganizations?.find(
              (subOrg: { location: { id: string } }) =>
                subOrg?.location?.id === values?.locationId
            )?.location?.preferredTimeZone &&
            values?.teamMember?.subOrganizations?.find(
              (subOrg: { location: { id: string } }) =>
                subOrg?.location?.id === values?.locationId
            )?.location?.preferredTimeZone !== userTimeZone &&
            values?.date &&
            selectedTimeSlot?.startDate &&
            !isRangeOfDates ? (
              <Typography fontSize={14}>{`${t(
                'Date and time in location'
              )}: ${formatInTimeZone(
                selectedTimeSlot?.startDate,
                values?.teamMember?.subOrganizations?.find(
                  (subOrg: { location: { id: string } }) =>
                    subOrg?.location?.id === values?.locationId
                )?.location?.preferredTimeZone,
                'P p',
                {
                  //@ts-ignore
                  locale: dateFnsLocales[i18n.language],
                }
              )}`}</Typography>
            ) : null}
          </FormControl>
        )}

        <MuiBox data-id="accept-on-behalf-container" display="flex" alignItems="center" mb="15px">
          <Field name="acceptOnBehalfOfPatient" defaultValue={false}>
            {({ input }) => (
              <Switch
                isBlue
                checked={Boolean(input.value)}
                onChange={(e: any) => {
                  input.onChange(e.target.checked);
                  input.onBlur();
                }}
              />
            )}
          </Field>
          <MuiBox ml={1}>
            <Typography variant="h5" style={{ fontWeight: 500 }}>
              {t('Doctor can accept on behalf of patient')}
            </Typography>
          </MuiBox>
        </MuiBox>

        <FormControl label={t('Note')} data-id="note-container">
          <FormInput
            name="note"
            placeholder={t('Add a note')}
            multiline
            maxRows={4}
            fullWidth={true}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="sticky-note" style={{ marginLeft: 10 }} />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>

        <FormControl label={t('File')} data-id="file-upload-container">
          <FormFileUploader
            placeholder={'Upload file (Accepted file: png,jpeg,pdf)'}
            acceptedTypesProps="image/jpeg,image/jpg,image/png,application/pdf"
            withTitle={false}
            name="files"
            customValidation={undefined}
          />
        </FormControl>
      </FormFieldsContainer>
      {chooseDateDialog?.isOpen && (
        <ChooseDateDialog
          isOpen={chooseDateDialog.isOpen}
          close={chooseDateDialog.close}
          initialData={chooseDateDialog.initialData}
          userId={uid}
        />
      )}
    </>
  );
};

export default AppointmentForm;

interface Props {
  initialData: any;
  form: FormApi;
  isEditMode: boolean;
  calendarFilter: any;
  setSearch: any;
  fetchMorePractitioners: any;
  orgUnitMembers: any;
}

const STypography = styled(Typography)`
  && {
    margin: 0;
    width: 320px;
    color: ${({theme}) => theme.palette.colors.gray400};
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
    text-align: center;
  }
`;
